import type { FxRate, PriceSource, Quote } from '@porttang/core';
import { collection, doc, getDoc, getDocs, Timestamp, type DocumentData } from 'firebase/firestore';
import { firestore } from '../../shared/firebase/client';

function toDate(value: unknown): Date | null {
  return value instanceof Timestamp ? value.toDate() : null;
}

function toQuote(priceKey: string, data: DocumentData): Quote | null {
  const price: unknown = data['price'];
  const currency: unknown = data['currency'];
  const fetchedAt = toDate(data['fetchedAt']);

  // เอกสารราคาที่ไม่ครบถือว่าไม่มีราคา ไม่ใช่ราคาศูนย์ — ปล่อยให้ core คืน null ต่อไป
  if (typeof price !== 'string' || typeof currency !== 'string' || fetchedAt === null) {
    return null;
  }

  const previousClose: unknown = data['previousClose'];
  const source: unknown = data['source'];

  return {
    symbol: typeof data['symbol'] === 'string' ? data['symbol'] : priceKey,
    source: (source === 'yahoo' || source === 'coingecko' || source === 'finnhub'
      ? source
      : 'yahoo') as PriceSource,
    price,
    currency,
    previousClose: typeof previousClose === 'string' ? previousClose : null,
    fetchedAt,
  };
}

/**
 * อ่านราคาของ priceKey ที่ระบุ
 *
 * ยิงทีละเอกสารแบบขนาน ไม่ใช้ `where(documentId(), 'in', ...)` เพราะ Firestore
 * จำกัดจำนวนค่าใน `in` และ priceKey มี `:` ซึ่งใช้ใน query แบบนั้นได้ยุ่งยาก
 * พอร์ตส่วนบุคคลมีไม่กี่สิบรายการ การอ่านขนานจึงเร็วพออยู่แล้ว
 */
export async function fetchQuotes(priceKeys: readonly string[]): Promise<Map<string, Quote>> {
  const unique = [...new Set(priceKeys)];
  const snapshots = await Promise.all(unique.map((key) => getDoc(doc(firestore, 'prices', key))));

  const quotes = new Map<string, Quote>();
  for (const snapshot of snapshots) {
    if (!snapshot.exists()) {
      continue;
    }
    const quote = toQuote(snapshot.id, snapshot.data());
    if (quote !== null) {
      quotes.set(snapshot.id, quote);
    }
  }
  return quotes;
}

/**
 * อ่านอัตราแลกเปลี่ยนทั้งหมด
 *
 * ดึงทั้ง collection เพราะมันเล็กมาก (คู่เงินไม่กี่คู่) และแบบนี้ฝั่งเว็บไม่ต้อง
 * คำนวณเองว่าต้องใช้คู่ไหน ซึ่งเป็น logic ที่ซ้ำกับฝั่ง function โดยไม่จำเป็น
 */
export async function fetchFxRates(): Promise<Map<string, FxRate>> {
  const snapshot = await getDocs(collection(firestore, 'fx'));
  const rates = new Map<string, FxRate>();

  for (const document of snapshot.docs) {
    const data = document.data();
    const rate: unknown = data['rate'];
    const fetchedAt = toDate(data['fetchedAt']);
    if (typeof rate !== 'string' || fetchedAt === null) {
      continue;
    }
    rates.set(document.id, { pair: document.id, rate, fetchedAt });
  }

  return rates;
}
